import React, { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getStorage } from "firebase/storage";
import NewNavbar from "../../new-components/NewNavbar";
import NewPageHeader from "../../new-components/NewPageHeader";
import NewFooter from "../../new-components/NewFooter";
import NewMadeWith from "../../new-components/NewMadeWith";
import NewOverrides from "../../new-components/NewOverrides";
import NewDesignSystem from "../../new-components/NewDesignSystem";
import NewScrollTop from "../../new-components/NewScrollTop";
import NewBackButton from "../../new-components/NewBackButton";
import "./style.css";

const EventGallery = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const { user } = props.user || {};
  const isAdmin =
    user?.email === process.env.REACT_APP_ADMIN ||
    user?.email === process.env.REACT_APP_ARI_ADMIN;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/gallery/get?campaignId=${id}`
      );
      if (res.data?.success) {
        setItems(res.data.data || []);
      }
    } catch (err) {
      console.error("Gallery fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const storage = getStorage();
      for (const file of files) {
        const isVideo = file.type.startsWith("video/");
        const type = isVideo ? "video" : "image";
        const storageRef = ref(storage, `gallery/${id}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/gallery/post?campaignId=${id}`,
          { url, type, thumbnail: "" }
        );
      }
      fetchGallery();
    } catch (err) {
      console.error("Gallery upload error:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (idsToDelete) => {
    if (!idsToDelete.length) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/gallery/delete?campaignId=${id}`,
        { data: { ids: idsToDelete } }
      );
      setSelectedIds([]);
      setSelectMode(false);
      fetchGallery();
    } catch (err) {
      console.error("Gallery delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (itemId) => {
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((i) => i.id));
    }
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const lightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
  }, [items.length]);

  const lightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i >= items.length - 1 ? 0 : i + 1));
  }, [items.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") lightboxPrev();
      else if (e.key === "ArrowRight") lightboxNext();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, lightboxPrev, lightboxNext]);

  if (loading) {
    return (
      <Fragment>
        <NewNavbar />
        <NewOverrides />
        <NewDesignSystem />
        <NewPageHeader />
        <div className="gallery">
          <div className="gallery__loader">
            <div className="gallery__loader-ring"><div></div><div></div><div></div></div>
            <span className="gallery__loader-text">Se încarcă galeria...</span>
          </div>
        </div>
        <NewFooter />
        <NewMadeWith />
        <NewScrollTop />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <NewNavbar />
      <NewOverrides />
      <NewDesignSystem />
      <NewPageHeader />

      <div className="gallery">
        <div className="gallery__container">
          {/* Header */}
          <div className="gallery__header">
            <div className="gallery__title-area">
              <h1 className="gallery__title">Galerie</h1>
              <p className="gallery__count">{items.length} {items.length === 1 ? "element" : "elemente"}</p>
            </div>
          </div>

          {/* Admin Controls */}
          {isAdmin && (
            <div className="gallery__admin-bar">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <button
                className="gallery__admin-btn gallery__admin-btn--add"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span className="gallery__upload-spinner"></span>
                    Se încarcă...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Adaugă
                  </>
                )}
              </button>
              <button
                className={`gallery__admin-btn ${selectMode ? "gallery__admin-btn--active" : ""}`}
                onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                Selectează
              </button>
              {selectMode && (
                <>
                  <button className="gallery__admin-btn" onClick={selectAll}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <polyline points="9 11 12 14 22 4" />
                    </svg>
                    {selectedIds.length === items.length ? "Deselectează tot" : "Selectează tot"}
                  </button>
                  {selectedIds.length > 0 && (
                    <button
                      className="gallery__admin-btn gallery__admin-btn--delete"
                      onClick={() => handleDelete(selectedIds)}
                      disabled={deleting}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-2 14H7L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4h6v2" />
                      </svg>
                      Șterge ({selectedIds.length})
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Grid */}
          {items.length === 0 ? (
            <div className="gallery__empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#b89aaa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p>Galeria este goală.</p>
              {isAdmin && <p className="gallery__empty-hint">Apasă „Adaugă" și selectează imagini sau videoclipuri de pe dispozitiv.</p>}
            </div>
          ) : (
            <div className="gallery__grid">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className={`gallery__item ${selectMode ? "gallery__item--selectable" : "gallery__item--clickable"} ${selectedIds.includes(item.id) ? "gallery__item--selected" : ""}`}
                  onClick={() => {
                    if (selectMode) toggleSelect(item.id);
                    else openLightbox(index);
                  }}
                >
                  {selectMode && (
                    <div className="gallery__item-check">
                      {selectedIds.includes(item.id) && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  )}
                  {item.type === "video" ? (
                    <div className="gallery__item-video">
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt="" className="gallery__item-img" />
                      ) : (
                        <video src={item.url} className="gallery__item-img" muted />
                      )}
                      <div className="gallery__item-play">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <img src={item.url} alt="" className="gallery__item-img" />
                  )}
                  {!selectMode && isAdmin && (
                    <button
                      className="gallery__item-delete"
                      onClick={(e) => { e.stopPropagation(); handleDelete([item.id]); }}
                      aria-label="Șterge"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NewBackButton />
      <NewFooter />
      <NewMadeWith />
      <NewScrollTop />

      {/* Lightbox */}
      {lightboxIndex !== null && items.length > 0 && (
        <div className="gallery__lightbox" role="dialog" aria-modal="true">
          <div className="gallery__lightbox-backdrop" onClick={closeLightbox} />

          {/* Close */}
          <button className="gallery__lightbox-close" onClick={closeLightbox} aria-label="Închide">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Prev */}
          {items.length > 1 && (
            <button className="gallery__lightbox-arrow gallery__lightbox-arrow--prev" onClick={lightboxPrev} aria-label="Anterior">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}

          {/* Media */}
          <div className="gallery__lightbox-inner">
            {items[lightboxIndex].type === "video" ? (
              <video
                src={items[lightboxIndex].url}
                className="gallery__lightbox-video"
                controls
                autoPlay
              />
            ) : (
              <img
                src={items[lightboxIndex].url}
                alt=""
                className="gallery__lightbox-img"
              />
            )}
          </div>

          {/* Next */}
          {items.length > 1 && (
            <button className="gallery__lightbox-arrow gallery__lightbox-arrow--next" onClick={lightboxNext} aria-label="Următor">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          {/* Counter */}
          {items.length > 1 && (
            <div className="gallery__lightbox-counter">
              {lightboxIndex + 1} / {items.length}
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(EventGallery);
